import {
  Button,
  Checkbox,
  IconButton,
  Select,
  TextField
} from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SecurityEventLogFilters.css';
import { getCategoryLabel, getEventTypeLabel } from './utils';

const EVENT_TYPES = [
  'member_removed',
  'product_access_changed',
  'bulk_product_access_removed',
  'bulk_product_access_updated',
  'invitation_sent',
  'invitation_revoked',
  'invitation_accepted',
  'invitation_resent',
  'enterprise_invitation_sent',
  'admin_removed',
  'subscription_changed',
  'team_created',
  'team_deleted',
  'team_membership_changed',
  'user_role_changed',
  'user_added',
  'user_edited',
  'user_removed',
  'settings_changed',
  'user_settings_changed',
  'api_key_created',
  'api_key_updated',
  'api_key_deleted',
  'security_log_exported'
];

// Phase 1 emits only access-control and system-configuration events. The
// `authentication` category is defined in the backend type map for future
// Keycloak-sourced login/logout events, but nothing in the app currently
// writes those events, so filtering by `authentication` would always return
// an empty result set. Re-add it here once login/logout/login_failed are
// actually emitted.
const CATEGORIES = ['access_control', 'system_configuration'];

export interface FilterState {
  startDate: string;
  endDate: string;
  eventType: string;
  category: string;
  actorId: string;
  adminOnly: boolean;
}

export const isDefaultFilterState = (f: FilterState): boolean =>
  !f.startDate &&
  !f.endDate &&
  !f.eventType &&
  !f.category &&
  !f.actorId &&
  !f.adminOnly;

interface SecurityEventLogFiltersProps {
  pending: FilterState;
  applied: FilterState;
  onChange: <K extends keyof FilterState>(
    field: K,
    value: FilterState[K]
  ) => void;
  onApply: () => void;
  onClear: () => void;
  onExport: () => void;
  onRefresh: () => void;
}

const SecurityEventLogFilters: React.FC<SecurityEventLogFiltersProps> = ({
  pending,
  applied,
  onChange,
  onApply,
  onClear,
  onExport,
  onRefresh
}) => {
  const { t } = useTranslation();

  const filtersChanged =
    pending.eventType !== applied.eventType ||
    pending.category !== applied.category ||
    pending.actorId !== applied.actorId ||
    pending.startDate !== applied.startDate ||
    pending.endDate !== applied.endDate ||
    pending.adminOnly !== applied.adminOnly;

  // Clear is meaningful only when there's something to clear in either the
  // staged edits or the applied state.
  const canClear =
    !isDefaultFilterState(pending) || !isDefaultFilterState(applied);

  return (
    <div className={styles.filters}>
      <div className={styles.filterGroup}>
        <TextField
          label={t('Start Date')}
          type="date"
          value={pending.startDate}
          onChange={(value: string) => onChange('startDate', value)}
        />
      </div>
      <div className={styles.filterGroup}>
        <TextField
          label={t('End Date')}
          type="date"
          value={pending.endDate}
          onChange={(value: string) => onChange('endDate', value)}
        />
      </div>
      <div className={styles.filterGroup}>
        <Select
          label={t('Event Type')}
          value={pending.eventType}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            onChange('eventType', e.target.value)
          }
          options={[
            { key: '', value: '', label: t('All') },
            ...EVENT_TYPES.map(type => ({
              key: type,
              value: type,
              label: getEventTypeLabel(t, type)
            }))
          ]}
        />
      </div>
      <div className={styles.filterGroup}>
        <Select
          label={t('Category')}
          value={pending.category}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            onChange('category', e.target.value)
          }
          options={[
            { key: '', value: '', label: t('All') },
            ...CATEGORIES.map(cat => ({
              key: cat,
              value: cat,
              label: getCategoryLabel(t, cat)
            }))
          ]}
        />
      </div>
      <div className={styles.filterGroup}>
        <TextField
          label={t('Actor User ID')}
          placeholder={t('Enter user UUID')}
          value={pending.actorId}
          onChange={(value: string) => onChange('actorId', value)}
        />
      </div>
      <div className={styles.adminOnlyGroup}>
        <Checkbox
          id="security-event-log-admin-only"
          label={t('Admin events only')}
          checked={pending.adminOnly}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange('adminOnly', e.target.checked)
          }
        />
      </div>
      <div className={styles.filterActions}>
        <Button
          variant="primary"
          thin
          onClick={onApply}
          disabled={!filtersChanged}
        >
          {t('Apply')}
        </Button>
        <IconButton
          icon="close"
          label={t('Clear Filters')}
          onClick={onClear}
          disabled={!canClear}
        />
        <IconButton icon="run-again" label={t('Refresh')} onClick={onRefresh} />
        <IconButton icon="download" label={t('Export')} onClick={onExport} />
      </div>
    </div>
  );
};

export default SecurityEventLogFilters;
